/**
 * RadioGroup — accessible radio button group built on Radix UI.
 *
 * Arrow-key navigation between items, roving tabindex, and ARIA
 * radiogroup semantics are handled by Radix. Visual treatment uses
 * Tailwind utilities; the selected state is surfaced via Radix's
 * `data-state="checked"` attribute on each item.
 *
 * Usage:
 *
 *     <RadioGroup value={mode} onValueChange={setMode}>
 *       <RadioGroupItem id="immune" value="immune" />
 *       <label htmlFor="immune">IMMUNE</label>
 *     </RadioGroup>
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
import { cn } from "../../styling/index.js";

export type RadioGroupProps = React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>;
export type RadioGroupItemProps = React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item>;

export const RadioGroup = React.forwardRef<
  React.ComponentRef<typeof RadioGroupPrimitive.Root>,
  RadioGroupProps
>(function RadioGroup({ className, ...props }, ref) {
  return <RadioGroupPrimitive.Root ref={ref} className={cn("grid gap-2", className)} {...props} />;
});

export const RadioGroupItem = React.forwardRef<
  React.ComponentRef<typeof RadioGroupPrimitive.Item>,
  RadioGroupItemProps
>(function RadioGroupItem({ className, ...props }, ref) {
  return (
    <RadioGroupPrimitive.Item
      ref={ref}
      className={cn(
        "aspect-square size-4 rounded-full border border-neutral-400 text-primary-700",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500",
        "disabled:cursor-not-allowed disabled:opacity-50",
        "data-[state=checked]:border-primary-700",
        className,
      )}
      {...props}
    >
      <RadioGroupPrimitive.Indicator className="flex items-center justify-center">
        <span aria-hidden="true" className="block size-2 rounded-full bg-primary-700" />
      </RadioGroupPrimitive.Indicator>
    </RadioGroupPrimitive.Item>
  );
});
